import { useEffect, useState } from "react";
import { X, MessageSquarePlus, Bug, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function FeedbackModal({ open, onClose }) {
  const [kind, setKind] = useState("improvement");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setBusy(true);
    try {
      await api.post("/feedback", {
        kind,
        message: message.trim(),
        page: typeof window !== "undefined" ? window.location.pathname : "",
        device: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : "",
      });
      toast.success("Thanks — we read every note.");
      setMessage("");
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send feedback.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm p-3 sm:items-center" data-testid="feedback-modal" role="dialog" aria-modal="true">
      <button aria-label="Close" onClick={onClose} className="absolute inset-0 -z-10" />
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-muted text-brand-teal">
              <MessageSquarePlus className="h-4 w-4" />
            </span>
            <p className="font-display text-sm font-semibold">Share feedback</p>
          </div>
          <button onClick={onClose} data-testid="feedback-close" className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3 p-5">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setKind("problem")} data-testid="feedback-kind-problem"
              className={`inline-flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold ${kind === "problem" ? "border-brand-red bg-brand-red/10 text-brand-red" : "border-border text-muted-foreground"}`}>
              <Bug className="h-3.5 w-3.5" /> Report a problem
            </button>
            <button type="button" onClick={() => setKind("improvement")} data-testid="feedback-kind-improvement"
              className={`inline-flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold ${kind === "improvement" ? "border-brand-orange bg-brand-orange/10 text-brand-orange" : "border-border text-muted-foreground"}`}>
              <Lightbulb className="h-3.5 w-3.5" /> Suggest an improvement
            </button>
          </div>
          <textarea
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            placeholder={kind === "problem" ? "What went wrong? Steps to reproduce, what you expected, what you saw." : "What would make Workspace better for you?"}
            className="w-full rounded-xl border border-border bg-background p-3 text-sm outline-none focus:border-primary"
            data-testid="feedback-message"
          />
          <p className="text-[11px] text-muted-foreground">We include the current page and your device automatically to help us reproduce issues.</p>
          <button
            type="submit"
            disabled={busy || !message.trim()}
            data-testid="feedback-submit"
            className="w-full rounded-full brand-gradient-bg px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send feedback"}
          </button>
        </form>
      </div>
    </div>
  );
}
